<template>
    <div class="card mb-3">
        <div class="section-title-bar">
            <h5 class="section-title">Submission</h5>
            <button @click="showAddModal = true" class="btn-add-new">
                <i class="fa fa-plus"></i> Add New
            </button>
        </div>

        <div class="section-body">
            <div class="row g-2 align-items-end mb-3">
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label class="filter-label">Select Month</label>
                    <vField
                        v-model="allocationMonth"
                        name="allocationMonth"
                        type="month"
                        class="form-control"
                    />
                </div>
                <div class="col-12 col-sm-auto">
                    <button
                        @click="getAllocationData"
                        class="btn btn-primary waves-effect waves-float w-100"
                    >GO</button>
                </div>
            </div>

            <div class="summary-cards" v-if="saleOrderAmount.sale_amount !== undefined">
                <div class="summary-card summary-sales">
                    <div class="summary-label">Total Sales</div>
                    <div class="summary-value">{{ commaFormat(saleOrderAmount.sale_amount) }}</div>
                </div>
                <div class="summary-card summary-allocated">
                    <div class="summary-label">Submitted Amount</div>
                    <div class="summary-value">{{ commaFormat(saleOrderAmount.total_sale_allocation) }}</div>
                </div>
                <div class="summary-card summary-remaining">
                    <div class="summary-label">Balance</div>
                    <div class="summary-value">{{ commaFormat(saleOrderAmount.remaining_balance) }}</div>
                </div>
            </div>

            <!-- Desktop table -->
            <div class="table-wrap desktop-table">
                <table class="data-table table table-bordered">
                    <thead>
                        <tr>
                            <th>Staff ID</th>
                            <th>Staff Name</th>
                            <th>Referrer Name & ID</th>
                            <th class="text-right">Allocation Amount</th>
                            <th class="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="allocationItems.length === 0 && !allocationLoading">
                            <td colspan="5" class="empty-row">No data available</td>
                        </tr>
                        <tr v-for="(item, i) in allocationItems" :key="i" class="data-row">
                            <td>{{ item.serial_no }}</td>
                            <td>{{ item.name }}</td>
                            <td>{{ item.parent_name }} {{ item.parent_serial_no ? '(' + item.parent_serial_no + ')' : '' }}</td>
                            <td class="text-right">{{ item.allocation_amount ?? '—' }}</td>
                            <td class="text-center">
                                <button
                                    v-if="item.allocation_amount != null && item.sale_allocationable_type == null"
                                    @click.stop="openEditModal(item)"
                                    class="btn btn-primary btn-sm"
                                    title="Edit Allocation"
                                ><i class="fa fa-pencil"></i></button>
                                <button
                                    v-if="item.allocation_amount != null && item.sale_allocationable_type == null"
                                    :disabled="allocationLoading"
                                    @click.stop="onDeleteItemHandler(item)"
                                    class="btn btn-danger btn-sm ms-1"
                                    title="Delete Allocation"
                                ><i class="fa fa-trash"></i></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mobile cards -->
            <div class="mobile-cards">
                <div v-if="allocationItems.length === 0 && !allocationLoading" class="mobile-empty">
                    No data available
                </div>
                <div
                    v-for="(item, i) in allocationItems"
                    :key="'m' + i"
                    class="sale-card"
                >
                    <div class="sale-card__header">
                        <span class="sale-card__id">ID: {{ item.serial_no }}</span>
                        <span class="sale-card__name">Name: {{ item.name }}</span>
                    </div>
                    <div class="sale-card__body">
                        <div class="sale-card__row">
                            <span class="sale-card__label">Referrer</span>
                            <span class="sale-card__val">{{ item.parent_name }} {{ item.parent_serial_no ? '(' + item.parent_serial_no + ')' : '' }}</span>
                        </div>
                        <div class="sale-card__row">
                            <span class="sale-card__label">Allocation Amount</span>
                            <span class="sale-card__val">{{ item.allocation_amount }}</span>
                        </div>
                        <div v-if="item.allocation_amount != null && item.sale_allocationable_type == null" class="sale-card__row">
                            <button @click="openEditModal(item)" class="btn-action btn-sm w-100">
                                <i class="fa fa-pencil"></i> Edit
                            </button>
                            <button :disabled="allocationLoading" @click.stop="onDeleteItemHandler(item)" class="btn btn-danger btn-sm w-100">
                                <i class="fa fa-trash"></i> Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Loader v-if="allocationLoading" />
    </div>

    <AddSubmissionModal
        v-if="showAddModal"
        :staff-id="allocationStaffId"
        :month="allocationMonth"
        @close="showAddModal = false"
        @saved="getAllocationData"
    />

    <EditSubmissionModal
        v-if="showEditModal"
        :month="allocationMonth"
        :item="editItem"
        :contact-profile="contactProfileData"
        @close="showEditModal = false"
        @saved="getAllocationData"
    />
</template>

<script setup>
import {inject, onMounted, ref, watch} from 'vue';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import AddSubmissionModal from './AddSubmissionModal.vue';
import EditSubmissionModal from './EditSubmissionModal.vue';
import handleSdm from '@/services/modules/sdm/sdm';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    staffId: {
      type: [Number, String],
      default: null
    },
    defaultMonth: {
      type: String,
      default: ''
    },
});

const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { commaFormat } = figureFormatter();
const { fetchStaffSaleAllocations, deleteSaleAllocation } = handleSdm();

const allocationStaffId = ref(props.staffId);
const allocationMonth = ref(props.defaultMonth);
const allocationItems = ref([]);
const saleOrderAmount = ref({});
const contactProfileData = ref(null);
const allocationLoading = ref(false);
const showAddModal = ref(false);
const showEditModal = ref(false);
const editItem = ref(null);

watch(() => props.staffId, (val) => {
    allocationStaffId.value = val;
});

const getAllocationQuery = () => {
    let q = `?company_id=${route.params.companyId}`;
    q += `&staff_id=${allocationStaffId.value}`;
    q += `&month=${allocationMonth.value}`;
    return q;
};

const openEditModal = (item) => {
    editItem.value = item;
    showEditModal.value = true;
};

const onDeleteItemHandler = (item) => {
    if (!confirm('Are you sure you want to delete this allocation?')) return
    allocationLoading.value = true
    deleteSaleAllocation(item.sale_allocation_id, `?company_id=${route.params.companyId}`)
        .then(res => {
            if (!res.status) {
                showError(res.message)
                return
            }
            showSuccess(res.message)
            getAllocationData()
        })
        .finally(() => { allocationLoading.value = false })
};

const getAllocationData = async () => {
    allocationLoading.value = true;
    allocationItems.value = [];
    saleOrderAmount.value = {};
    contactProfileData.value = null;
    await fetchStaffSaleAllocations(getAllocationQuery()).then(res => {
        if (!res.status) {
          return;
        }
        allocationItems.value = res.data.data;
        saleOrderAmount.value = res.data.sale_order;
        contactProfileData.value = res.data.contact_profile;
    }).catch(() => {}).finally(() => { allocationLoading.value = false; });
};

onMounted(async () => {
  await getAllocationData()
})
</script>

<style scoped>
.section-title-bar {
    background: linear-gradient(90deg, #1e3a5f 0%, #2d6a9f 100%);
    padding: 0.75rem 1.25rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.section-title {
    margin: 0;
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.03em;
}

.btn-add-new {
    background: rgb(255 255 255 / 0.98);
    border: 1px solid rgba(255,255,255,0.4);
    color: #000000;
    font-size: 0.8rem;
    font-weight: 600;
    padding: 0.5rem 1.75rem;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    transition: background 0.15s;
    white-space: nowrap;
}

.btn-add-new:hover {
    background: rgba(255,255,255,0.28);
}

.section-body {
    padding: 1.25rem;
}

.filter-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.summary-cards {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
}

.summary-card {
    flex: 1;
    min-width: 160px;
    border-radius: 12px;
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
}

.summary-sales {
    background: linear-gradient(135deg, #dbeafe, #eff6ff);
    border-left: 4px solid #3b82f6;
}

.summary-allocated {
    background: linear-gradient(135deg, #fce7f3, #fdf2f8);
    border-left: 4px solid #ec4899;
}

.summary-remaining {
    background: linear-gradient(135deg, #d1fae5, #ecfdf5);
    border-left: 4px solid #10b981;
}

.summary-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.summary-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
}

.table-wrap {
    overflow-x: auto;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.data-table thead tr {
    background: linear-gradient(90deg, #f1f5f9, #f8fafc);
}

.data-table th {
    padding: 0.75rem 1rem;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.data-table td {
    padding: 0.65rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    color: #374151;
}

.data-row:hover {
    background: #f8f9ff;
    cursor: pointer;
}

.empty-row {
    text-align: center;
    color: #9ca3af;
    padding: 2rem !important;
    font-style: italic;
}

.text-right { text-align: right; }
.text-center { text-align: center; }

.btn-action {
    background: rgba(30, 58, 95, 0.08);
    border: 1px solid rgba(30, 58, 95, 0.2);
    color: #1e3a5f;
    border-radius: 6px;
    padding: 0.25rem 0.55rem;
    font-size: 0.78rem;
    cursor: pointer;
    transition: background 0.15s;
}

.btn-action:hover {
    background: rgba(30, 58, 95, 0.18);
}

/* Mobile cards — hidden on desktop */
.mobile-cards { display: none; }

@media (max-width: 767px) {
    .desktop-table { display: none; }

    .mobile-cards {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .mobile-empty {
        text-align: center;
        color: #9ca3af;
        padding: 2rem;
        font-style: italic;
        border: 1px dashed #e5e7eb;
        border-radius: 10px;
    }

    .sale-card {
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    }

    .sale-card__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(90deg, #dbeafe, #eff6ff);
        padding: 0.6rem 1rem;
        gap: 0.5rem;
    }

    .sale-card__id {
        font-size: 0.75rem;
        font-weight: 700;
        color: #1e40af;
        white-space: nowrap;
    }

    .sale-card__name {
        font-size: 0.88rem;
        font-weight: 700;
        color: #1e3a5f;
        text-align: right;
    }

    .sale-card__body {
        padding: 0.75rem 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background: #fff;
    }

    .sale-card__row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
    }

    .sale-card__label {
        font-size: 0.73rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        white-space: nowrap;
    }

    .sale-card__val {
        font-size: 0.85rem;
        font-weight: 500;
        color: #1e293b;
        text-align: right;
    }

}
</style>
